import {
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getUserCategories } from 'store/categories/actions';
import {
  getUserCategories as getCategoriesSelector,
  getUserCategoriesLoadingState,
  getUserCategoriesOptions,
} from 'store/categories/selectors';
import useDebounce from 'library/hooks/use-debounce';
import { getLabelMap } from 'library/helpers/text';
import MiniSearch from 'minisearch';

// eslint-disable-next-line import/prefer-default-export
export const DEFAULT_SEARCH_CONFIGURATION = {
  fields: ['text'],
  idField: 'value',
  searchOptions: {
    prefix: true,
  },
};

export const getDefaultSearchResultMap = (result, { value }) => ({
  ...result,
  [value]: true,
}); // => { [item id]: true }

export const getSearchResultMap = (result, { id }) => ({
  ...result,
  [id]: true,
}); // => { [item id]: true }

const useCategoryList = ({
  skip = true,
  defaultSnapshot = {},
  areInactiveCategoriesIncluded = false,
}) => {
  const [query, onQueryChange] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const dispatch = useDispatch();
  const data = useSelector(getCategoriesSelector);
  const isLoading = useSelector(getUserCategoriesLoadingState);
  const options = useSelector(getUserCategoriesOptions);
  const labelMapSnapshot = useRef({});
  const search = useMemo(() => new MiniSearch(DEFAULT_SEARCH_CONFIGURATION), [options]);
  const [allowedOptionMap, setAllowedOptions] = useState({});

  useEffect(() => {
    if (!isLoading && !options.length) {
      dispatch(getUserCategories());
    }
  }, [isLoading, skip, options?.length]);

  useEffect(() => {
    if (options?.length > 0) {
      search.addAll(options);
      setAllowedOptions(options.reduce(getDefaultSearchResultMap, {}));
    }
  }, [options?.length > 0]);

  useEffect(() => {
    if (debouncedQuery) {
      const results = search.search(debouncedQuery);

      setAllowedOptions(results.reduce(getSearchResultMap, {}));
    } else {
      // if search input is empty, allow all options to be displayed
      setAllowedOptions(options.reduce(getDefaultSearchResultMap, {}));
    }
  }, [debouncedQuery]);

  return useMemo(() => {
    const labelMap = getLabelMap(data, {
      ...defaultSnapshot,
      ...labelMapSnapshot.current,
    });

    labelMapSnapshot.current = labelMap;

    return {
      data,
      isLoading,
      options: options.filter(({ value }) => allowedOptionMap[value] && (areInactiveCategoriesIncluded ? true : data.find(({ id }) => id === value)?.active)),
      labelMap,
      onQueryChange,
    };
  }, [data, defaultSnapshot]);
};

export default useCategoryList;
